import config from '../config/physics.v1.json';
import lookup from '../config/trig.v1.json';
import type { Hook, Mineral } from './types.js';
// v1 config and LUT remain immutable for historical replay. Only motion/collision changes in v2.
export const PHYSICS = Object.freeze({ ...config, version: 'physics.v2', radii: Object.freeze(config.radii), progress: Object.freeze(config.progress), returnSpeeds: Object.freeze(config.returnSpeeds) });
export const physics = PHYSICS;
export const PHYSICS_VERSION = PHYSICS.version;
export const SCALE = PHYSICS.scale;
export const TICK_RATE = PHYSICS.tickRate;
export const A_TICKS = PHYSICS.aTicks;
export const B_TICKS = PHYSICS.bTicks;
export const DOCKED_DISTANCE = 78000;
/** Integer LUT lookup rounded to nearest 0.1 degree; no runtime trigonometry. */
export function directionAt(angle: number): { x: number; y: number } {
  if (!Number.isSafeInteger(angle) || angle < PHYSICS.minAngle || angle > PHYSICS.maxAngle) throw new Error('Invalid angle');
  const index = Math.round((angle - PHYSICS.minAngle) / PHYSICS.trigStepMilliDegrees);
  return { x: lookup.sin[index]!, y: lookup.cos[index]! };
}
export function positionAt(angle: number, distance: number): { x: number; y: number } {
  const direction = directionAt(angle);
  return { x: PHYSICS.originX + Math.trunc(direction.x * distance / PHYSICS.trigScale), y: PHYSICS.originY + Math.trunc(direction.y * distance / PHYSICS.trigScale) };
}
export interface SegmentCollision { object: Mineral; t: number; point: { x: number; y: number } }
const FRACTION = 1_000_000_000n;
function integerSqrt(n: bigint): bigint {
  if (n < 2n) return n;
  let x = n, next = (x + 1n) / 2n;
  while (next < x) { x = next; next = (x + n / x) / 2n; }
  return x;
}
/** First circle ENTRY, not the closest point. Integer discriminants and fixed-point roots
 * keep client/server/replay identical, including tangents. Equal entries break ties by id. */
export function collisionAlongSegment(start: { x: number; y: number }, end: { x: number; y: number }, objects: readonly Mineral[]): SegmentCollision | undefined {
  const dx = end.x - start.x, dy = end.y - start.y;
  const a = BigInt(dx * dx + dy * dy);
  let best: { object: Mineral; fraction: bigint } | undefined;
  for (const object of objects) {
    const r = object.radius;
    if (Math.max(start.x, end.x) < object.x - r || Math.min(start.x, end.x) > object.x + r || Math.max(start.y, end.y) < object.y - r || Math.min(start.y, end.y) > object.y + r) continue;
    const ox = start.x - object.x, oy = start.y - object.y;
    const c = BigInt(ox * ox + oy * oy - r * r);
    let fraction = 0n;
    if (c > 0n) {
      if (a === 0n) continue;
      const b = BigInt(ox * dx + oy * dy);
      const discriminant = b * b - a * c;
      if (b >= 0n || discriminant < 0n) continue;
      fraction = (-b * FRACTION - integerSqrt(discriminant * FRACTION * FRACTION)) / a;
      if (fraction > FRACTION) continue;
    }
    if (!best || fraction < best.fraction || fraction === best.fraction && object.id < best.object.id) best = { object, fraction };
  }
  if (!best) return undefined;
  const t = Number(best.fraction) / Number(FRACTION);
  return { object: best.object, t, point: { x: start.x + Math.trunc(dx * t), y: start.y + Math.trunc(dy * t) } };
}
export function collisionAt(hook: Pick<Hook, 'x' | 'y'>, objects: readonly Mineral[]): Mineral | undefined {
  return collisionAlongSegment(hook, hook, objects)?.object;
}
export function newHook(): Hook {
  return { status: 'SWINGING', angle: PHYSICS.initialAngle, direction: 1, distance: 0, ...positionAt(PHYSICS.initialAngle, DOCKED_DISTANCE), carry: null, shotPhase: null, fastReturn: false };
}
